import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, doc, setDoc, getDoc, updateDoc, serverTimestamp, functions, auth } from "@/firebase";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";

// Type definitions
interface Send2FACodeResponse {
  success: boolean;
  twoFAId: string;
  expiresAt: string;
  message?: string;
}

interface Verify2FACodeResponse {
  verified: boolean;
  message?: string;
  redirectUrl?: string;
}

interface Resend2FACodeResponse {
  success: boolean;
  message?: string;
  twoFAId?: string;
  expiresAt?: string;
}

export interface TwoFAData {
  userId: string;
  email: string;
  code: string;
  expiresAt: Date;
  attempts: number;
  verified: boolean;
  createdAt: Date;
  userType: 'admin' | 'superadmin';
}

class TwoFactorAuthService {
  private getCurrentUser() {
    const auth = getAuth();
    return auth.currentUser;
  }

  /**
   * Generate a 6-digit 2FA code
   */
  private generate2FACode(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  /**
   * Send 2FA code via email
   */
  async send2FACode(
    email: string,
    userType: 'admin' | 'superadmin'
  ): Promise<Send2FACodeResponse> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        logError('User not authenticated when trying to send 2FA', new Error('No current user'));
        throw new Error("User not authenticated");
      }

      // Force token refresh to ensure it's valid
      const token = await user.getIdToken(true);
      logDebug('Sending 2FA code to email', { 
        userType,
        hasUser: !!user,
        hasToken: !!token,
        uid: user.uid
      });

      // Call cloud function to send 2FA code
      const send2FACode = httpsCallable<
        { email: string; userType: string },
        Send2FACodeResponse
      >(functions, "send2FACode");

      const result = await send2FACode({
        email,
        userType,
      });

      const data = result.data;

      if (data.success) {
        logSuccess('2FA code sent successfully');
        
        // Store 2FA ID in sessionStorage for later verification
        sessionStorage.setItem('pending2FAId', data.twoFAId);
        sessionStorage.setItem('pending2FAExpiry', data.expiresAt);
        
        return {
          success: true,
          twoFAId: data.twoFAId,
          expiresAt: data.expiresAt,
          message: 'Verification code sent to your email',
        };
      } else {
        throw new Error(data.message || 'Failed to send 2FA code');
      }
    } catch (error: any) {
      logError('Error sending 2FA code', error);
      throw new Error(error.message || 'Failed to send 2FA code');
    }
  }

  /**
   * Verify 2FA code
   * Updated to use direct HTTP request to fix CORS preflight issues
   */
  async verify2FACode(
    twoFAId: string,
    code: string,
    trustDevice: boolean = false
  ): Promise<Verify2FACodeResponse> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      logDebug('Verifying 2FA code');

      // Get Firebase ID token for authentication
      const idToken = await user.getIdToken();

      // Call cloud function directly with explicit CORS
      const functionUrl = 'https://us-central1-elevated-18db4.cloudfunctions.net/verify2FACode';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          data: {
            twoFAId,
            code,
            trustDevice,
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      const data = responseData.result || responseData;

      if (data.verified) {
        logSuccess('2FA code verified successfully');
        
        // Clear pending 2FA from sessionStorage
        sessionStorage.removeItem('pending2FAId');
        sessionStorage.removeItem('pending2FAExpiry');
        
        return {
          verified: true,
          message: 'Verification successful',
          redirectUrl: data.redirectUrl,
        };
      } else {
        return {
          verified: false,
          message: data.message || 'Invalid verification code',
        };
      }
    } catch (error: any) {
      logError('Error verifying 2FA code', error);
      return {
        verified: false,
        message: error.message || 'Verification failed',
      };
    }
  }

  /**
   * Resend 2FA code
   */
  async resend2FACode(twoFAId: string): Promise<Resend2FACodeResponse> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        logError('User not authenticated when trying to resend 2FA', new Error('No current user'));
        throw new Error("User not authenticated");
      }

      // Get Firebase ID token for authentication
      const idToken = await user.getIdToken();
      logDebug('Resending 2FA code', { 
        hasUser: !!user, 
        hasToken: !!idToken, 
        uid: user.uid,
        twoFAId 
      });

      // Call cloud function directly with explicit CORS
      const functionUrl = 'https://us-central1-elevated-18db4.cloudfunctions.net/resend2FACode';
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          data: {
            twoFAId,
          }
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      const data = responseData.result || responseData;

      if (data.success) {
        logSuccess('2FA code resent successfully');
        
        // Update sessionStorage with new expiry
        if (data.expiresAt) {
          sessionStorage.setItem('pending2FAExpiry', data.expiresAt);
        }
        
        return {
          success: true,
          message: 'New code sent to your email',
          twoFAId: data.twoFAId,
          expiresAt: data.expiresAt,
        };
      } else {
        throw new Error(data.message || 'Failed to resend 2FA code');
      }
    } catch (error: any) {
      logError('Error resending 2FA code', error);
      throw new Error(error.message || 'Failed to resend 2FA code');
    }
  }

  /**
   * Clear 2FA verification from sessionStorage
   */
  clear2FAVerification(): void {
    sessionStorage.removeItem('pending2FAId');
    sessionStorage.removeItem('pending2FAExpiry');
    logDebug('2FA verification cleared from sessionStorage');
  }

  /**
   * Check if there's a pending 2FA verification
   */
  hasPending2FA(): { twoFAId: string; expiresAt: string } | null {
    const twoFAId = sessionStorage.getItem('pending2FAId');
    const expiresAt = sessionStorage.getItem('pending2FAExpiry');
    
    if (twoFAId && expiresAt) {
      // Check if not expired
      if (new Date(expiresAt) > new Date()) {
        return { twoFAId, expiresAt };
      } else {
        // Clear expired 2FA
        this.clear2FAVerification();
      }
    }
    
    return null;
  }

  /**
   * Store 2FA verification attempt in Firestore (called by cloud function)
   * This is a helper method for server-side use
   */
  async store2FAVerification(
    twoFAId: string,
    userId: string,
    email: string,
    code: string,
    userType: 'admin' | 'superadmin',
    expirationMinutes: number = 10
  ): Promise<void> {
    try {
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + expirationMinutes);

      const twoFAData: TwoFAData = {
        userId,
        email,
        code,
        expiresAt,
        attempts: 0,
        verified: false,
        createdAt: new Date(),
        userType,
      };

      await setDoc(doc(db, 'twoFactorVerifications', twoFAId), {
        ...twoFAData,
        createdAt: serverTimestamp(),
        expiresAt: expiresAt,
      });

      logDebug('2FA verification stored in Firestore', { twoFAId });
    } catch (error: any) {
      logError('Error storing 2FA verification', error);
      throw new Error('Failed to store 2FA verification');
    }
  }

  /**
   * Get 2FA verification from Firestore
   */
  async get2FAVerification(twoFAId: string): Promise<TwoFAData | null> {
    try {
      const docRef = doc(db, 'twoFactorVerifications', twoFAId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as TwoFAData;
      }

      return null;
    } catch (error: any) {
      logError('Error getting 2FA verification', error);
      return null;
    }
  }

  /**
   * Update 2FA verification attempt count
   */
  async increment2FAAttempts(twoFAId: string): Promise<number> {
    try {
      const docRef = doc(db, 'twoFactorVerifications', twoFAId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentAttempts = docSnap.data().attempts || 0;
        const newAttempts = currentAttempts + 1;

        await updateDoc(docRef, {
          attempts: newAttempts,
          lastAttemptAt: serverTimestamp(),
        });

        return newAttempts;
      }

      return 0;
    } catch (error: any) {
      logError('Error incrementing 2FA attempts', error);
      return 0;
    }
  }

  /**
   * Mark 2FA as verified
   */
  async mark2FAAsVerified(twoFAId: string): Promise<void> {
    try {
      const docRef = doc(db, 'twoFactorVerifications', twoFAId);
      await updateDoc(docRef, {
        verified: true,
        verifiedAt: serverTimestamp(),
      });

      logSuccess('2FA marked as verified', { twoFAId });
    } catch (error: any) {
      logError('Error marking 2FA as verified', error);
      throw new Error('Failed to mark 2FA as verified');
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let twoFactorAuthServiceInstance: TwoFactorAuthService | null = null;

export function getTwoFactorAuthService(): TwoFactorAuthService {
  if (!twoFactorAuthServiceInstance) {
    twoFactorAuthServiceInstance = new TwoFactorAuthService();
  }
  return twoFactorAuthServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const twoFactorAuthService = new Proxy({} as TwoFactorAuthService, {
  get(target, prop) {
    return getTwoFactorAuthService()[prop as keyof TwoFactorAuthService];
  }
});
